import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { deepgramService, type TranscriptionResult } from "@/services/deepgramService";
import { generateSpeechStream } from "@/services/textToSpeechService";
import { useToast } from "@/hooks/use-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faMicrophone,
    faSquare,
    faXmark,
    faSliders,
    faSpinner,
    faPlay,
    faPause,
    faEllipsisVertical
} from "@fortawesome/free-solid-svg-icons";
import VoiceModeStartTone from "@/assets/Audio/VoiceModeStartTone.mp3"
import VoiceModeExitTone from "@/assets/Audio/VoiceModeExitTone.mp3"
import { ShimmerText } from "@/components/ShimmerText";
import { groqFetch, deepgramTtsFetch } from "@/services/voiceApiKeyManager";
import { searchWeb } from "@/services/tavilyService";
import { supabase } from "@/integrations/supabase/client";
import { Settings2, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import BotResponse from "./BotResponse";
import { useIsMobile } from "@/hooks/use-mobile";
import { jsPDF } from "jspdf";

// Microphone enable instructions carousel with icons
const MIC_ENABLE_INSTRUCTIONS = [
    { title: "Open Site Settings", description: "Click the lock/info icon (🔒) in your browser's address bar, next to the URL", icon: "🔒" },
    { title: "Find Microphone", description: "Look for 'Site settings' or 'Permissions' in the dropdown menu", icon: "⚙️" },
    { title: "Allow Access", description: "Find 'Microphone' and change it from 'Block' to 'Allow'", icon: "🎤" },
    { title: "Refresh & Retry", description: "Refresh this page and open voice mode again", icon: "🔄" },
];


// Component for streaming AI text display with optional truncation
const StreamingCardContent = ({
    content,
    isStreaming,
    isDarkMode,
    maxLength = 10000
}: {
    content: string;
    isStreaming: boolean;
    isDarkMode: boolean;
    maxLength?: number;
}) => {
    const [displayedText, setDisplayedText] = React.useState("");
    const indexRef = React.useRef(0);
    const prevContentRef = React.useRef("");

    // Truncate content for display only
    const truncatedContent = content.length > maxLength
        ? content.slice(0, maxLength) + "..."
        : content;

    React.useEffect(() => {
        if (!isStreaming || !content) {
            if (!isStreaming) {
                setDisplayedText(truncatedContent || "");
            }
            return;
        }

        // Reset if content changed significantly
        if (prevContentRef.current !== content) {
            indexRef.current = 0;
            setDisplayedText("");
            prevContentRef.current = content;
        }

        const interval = setInterval(() => {
            if (indexRef.current < truncatedContent.length) {
                const charsToAdd = Math.min(2, truncatedContent.length - indexRef.current);
                indexRef.current += charsToAdd;
                setDisplayedText(truncatedContent.slice(0, indexRef.current));
            } else {
                clearInterval(interval);
            }
        }, 15);

        return () => clearInterval(interval);
    }, [content, isStreaming, truncatedContent]);

    if (!displayedText && isStreaming) {
        return (
            <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-white/50' : 'bg-black/50'} animate-pulse`} />
                <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-white/50' : 'bg-black/50'} animate-pulse delay-75`} />
                <div className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-white/50' : 'bg-black/50'} animate-pulse delay-150`} />
            </div>
        );
    }

    return (
        <div className="relative">
            <BotResponse content={displayedText} className={isDarkMode ? "text-white prose-invert" : "text-black"} />
        </div>
    );
};

// Plasma Stop Button with Vortex Animation
const PlasmaStopButton = ({ isDarkMode }: { isDarkMode: boolean }) => {
    const plasmaWrapRef = React.useRef<HTMLDivElement>(null);
    const particlesRef = React.useRef<Array<{
        el: HTMLDivElement;
        angle: number;
        radius: number;
        speed: number;
        oscillation: number;
        size: number;
    }>>([]);
    const animationRef = React.useRef<number>(0);

    const palette = [
        '#ff4d4d', '#ff5a5a', '#ff6666', '#ff7373', '#ff8080',
        '#ff6b5c', '#ff7666', '#ff8170', '#ff8c7a', '#ff9784'
    ];

    React.useEffect(() => {
        const wrap = plasmaWrapRef.current;
        if (!wrap) return;

        // Clear existing particles
        wrap.innerHTML = '';
        particlesRef.current = [];

        // Create particles
        const particleCount = 10;
        for (let i = 0; i < particleCount; i++) {
            const el = document.createElement('div');
            el.style.position = 'absolute';
            el.style.borderRadius = '50%';
            el.style.willChange = 'transform';
            el.style.opacity = '0.8';

            const size = Math.random() * 18 + 10;
            el.style.width = size + 'px';
            el.style.height = size + 'px';

            const color = palette[i % palette.length];
            el.style.background = `radial-gradient(circle at 30% 30%, ${color} 0%, rgba(255,255,255,0.25) 25%, rgba(255,120,80,0.6) 45%, rgba(255,80,40,0.2) 65%, transparent 75%)`;
            el.style.boxShadow = `0 0 20px 5px ${color}, inset 0 0 10px rgba(255,255,255,0.25)`;

            wrap.appendChild(el);

            particlesRef.current.push({
                el,
                angle: Math.random() * Math.PI * 2,
                radius: Math.random() * 15 + 5,
                speed: Math.random() * 0.05 + 0.05,
                oscillation: Math.random() * 10,
                size
            });
        }

        // Animation loop
        const animate = () => {
            const time = Date.now() * 0.005;

            particlesRef.current.forEach((p, i) => {
                p.angle += p.speed;
                const currentRadius = p.radius + Math.sin(time * 1.2 + i) * 9 + Math.sin(time * 0.4 + i * 2) * 5;

                // Convert Polar to Cartesian (center is 26px for 52px button)
                const x = 26 + Math.cos(p.angle) * currentRadius;
                const y = 26 + Math.sin(p.angle) * currentRadius;

                const pulse = 1 + Math.sin(time * 2 + i) * 0.2;
                p.el.style.transform = `translate(${x - p.size / 2}px, ${y - p.size / 2}px) scale(${pulse})`;

                const hue = Math.sin(time * 0.15 + i) * 35 + Math.sin(time * 0.03) * 20;
                p.el.style.filter = `hue-rotate(${hue}deg) blur(1px)`;
            });

            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            cancelAnimationFrame(animationRef.current);
        };
    }, []);

    return (
        <div
            className="relative w-[52px] h-[52px] rounded-full border flex justify-center items-center overflow-hidden"
            style={{
                background: '#111112',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.9), 0 0 30px rgba(255, 69, 0, 0.1)'
            }}
        >
            {/* Aura Glow */}
            <div
                className="absolute rounded-full animate-aura-drift"
                style={{
                    inset: '-20%',
                    background: 'radial-gradient(circle, rgba(255,120,80,0.25), rgba(255,80,40,0.15), transparent 70%)',
                    filter: 'blur(15px)',
                    zIndex: 1
                }}
            />

            {/* Plasma Particles Container */}
            <div
                ref={plasmaWrapRef}
                className="absolute inset-0"
                style={{
                    filter: 'blur(7px) saturate(180%) contrast(140%) brightness(1.15)',
                    mixBlendMode: 'plus-lighter'
                }}
            />

            {/* Glass Overlay */}
            <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                    background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.1) 0%, transparent 60%)',
                    zIndex: 5
                }}
            />

            {/* Stop Icon */}
            <div
                className="relative z-10 w-[18px] h-[18px] bg-white rounded-[4px]"
                style={{ boxShadow: '0 0 15px rgba(255,255,255,0.4)' }}
            />
        </div>
    );
};

type VoiceModeState =
    | "connecting"
    | "idle"
    | "recording"
    | "transcribing"
    | "thinking"
    | "speaking"
    | "error";

interface VoiceModeOverlayProps {
    isOpen: boolean;
    isDarkMode: boolean;
    onClose: () => void;
    onOpenPlus?: () => void;
}

// GROQ API URL
const GROQ_API_URL = "https://api.groq.com/openai/v1/chat/completions";

// Maximum number of audio blobs to keep (for memory management)
const MAX_AUDIO_BLOBS = 2;

const sanitizeForTts = (text: string): string => {
    if (!text) return "";
    // Optimized single-pass regex to remove markdown symbols and clean up whitespace.
    // Removes: #, *, `, _, ~
    return text.replace(/[\#\*`_~]/g, "").replace(/\s+/g, " ").trim();
};

const mapVoiceToSarvamSpeaker = (voiceId: string): string => {
    const v = voiceId.toLowerCase();
    if (v.includes("aries")) return "aditya";
    if (v.includes("pluto")) return "rohan";
    if (v.includes("selene")) return "ritu";
    if (v.includes("vesta")) return "priya";
    return "aditya";
};

async function chatWithGroq(params: {
    messages: any[];
    temperature?: number;
    maxTokens?: number;
    signal: AbortSignal;
    conversationHistory?: { role: string; content: string }[];
    systemPromptOverride?: string;
}): Promise<string> {
    const { messages, temperature = 1, maxTokens = 6000, signal, conversationHistory = [], systemPromptOverride } = params;

    // Filter out messages with empty content if any, to be safe.
    const validMessages = messages.filter(m => m.content && m.content.trim() !== "");

    // Build a context summary from conversation history (memory)
    let memoryContext = "";
    if (conversationHistory.length > 0) {
        memoryContext = "\n\nHere is the recent conversation context for your reference (DO NOT answer these old questions again, they are just for context):\n";
        conversationHistory.forEach((msg, idx) => {
            const role = msg.role === "user" ? "User" : "Assistant";
            memoryContext += `${role}: ${msg.content}\n`;
        });
        memoryContext += "\n---END OF CONTEXT---\n\nNow respond ONLY to the latest user message below:";
    }

    // Use groqFetch with automatic key fallback
    try {
        const res = await groqFetch(GROQ_API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                model: "openai/gpt-oss-120b",
                // System prompt + truncated history
                messages: [
                    {
                        role: "system",
                        content: systemPromptOverride ||
                            `You are a helpful voice assistant. Be concise and direct. 
                            Prefer short answers (1-3 sentences) unless the user asks for details. 
                            Do NOT use any markdown symbols like asterisks (*), hashes (#), underscores (_), backticks (\`), or tildes (~).
                            Use ONLY well-formatted punctuation marks like double quotes ("), question marks (?), exclamation marks (!), periods (.), and commas (,).
                            Do NOT provide any citations or references (like [1], [Source]), even if you perform a search.
                            Simply answer the user's question directly and naturally.
                            
                            IMPORTANT: If the user asks you to write code, create code, generate code, debug code, explain code syntax, or anything that involves showing actual programming code, respond with something like:
                            "Let’s switch to text mode for this—tap the close button below. I’ll format the code for you there, as it’s much easier to follow visually than out loud."
                            ${memoryContext}`,
                    },
                    ...validMessages,
                ],
                temperature,
                max_completion_tokens: maxTokens,
                top_p: 1,
                stream: false,
                reasoning_effort: "medium",
            }),
            signal,
        });

        if (!res.ok) {
            const t = await res.text();
            throw new Error(t || "Voice LLM request failed");
        }

        const json = await res.json().catch(async () => {
            const t = await res.text();
            throw new Error(t || "Invalid Groq response");
        });
        const text =
            json?.choices?.[0]?.message?.content ||
            "";
        return (text || "").toString();
    } catch (groqError) {
        console.warn("Groq failed, falling back to SambaNova:", groqError);
        try {
            const systemContent = systemPromptOverride ||
                `You are a helpful voice assistant. Be concise and direct. 
                Prefer short answers (1-3 sentences) unless the user asks for details. 
                Do NOT use any markdown symbols like asterisks (*), hashes (#), underscores (_), backticks (\`), or tildes (~).
                Use ONLY well-formatted punctuation marks like double quotes ("), question marks (?), exclamation marks (!), periods (.), and commas (,).
                Do NOT provide any citations or references (like [1], [Source]), even if you perform a search.
                Simply answer the user's question directly and naturally.
                
                IMPORTANT: If the user asks you to write code, create code, generate code, debug code, explain code syntax, or anything that involves showing actual programming code, respond with something like:
                "Let’s switch to text mode for this—tap the close button below. I’ll format the code for you there, as it’s much easier to follow visually than out loud."
                ${memoryContext}`;

            const finalMessages = [
                { role: "system", content: systemContent },
                ...validMessages.filter(m => m.role !== 'system')
            ];

            const { data, error } = await supabase.functions.invoke('sambanova-chat', {
                body: {
                    messages: finalMessages,
                    model: "gpt-oss-120b",
                    stream: false,
                    temperature: temperature
                }
            });

            if (error || !data || !data.response) {
                throw new Error(error?.message || "SambaNova failed");
            }

            return data.response;
        } catch (sambaError) {
            console.error("SambaNova fallback failed too:", sambaError);
            throw new Error("Both Groq and SambaNova failed");
        }
    }
}

async function deepgramSpeak(params: {
    text: string;
    model: string;
    signal: AbortSignal;
}): Promise<HTMLAudioElement> {
    return generateSpeechStream(params.text, {
        provider: 'deepgram',
        voiceId: params.model,
        signal: params.signal
    });
}

const getDisplayUrl = (url: string) => {
    try {
        const parsed = new URL(url);
        const pathSnippet = parsed.pathname.length > 15 ? parsed.pathname.substring(0, 15) + '...' : parsed.pathname;
        return parsed.hostname + (pathSnippet === '/' ? '' : pathSnippet);
    } catch {
        return url.length > 25 ? url.substring(0, 25) + '...' : url;
    }
};

async function hardcodedTavilySearch(query: string): Promise<any> {
    try {
        const response = await fetch("https://api.tavily.com/search", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer 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"
            },
            body: JSON.stringify({
                api_key: "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",
                query: query,
                search_depth: "advanced",
                include_answer: true,
                include_raw_content: false,
                max_results: 5,
                include_images: true,
                include_image_descriptions: true,
                include_favicon: true
            })
        });

        if (!response.ok) {
            throw new Error(`Tavily HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return {
            query: data.query,
            answer: data.answer || "",
            results: (data.results || []).map((result: any) => ({
                title: result.title,
                url: result.url,
                content: result.content,
                favicon: result.favicon,
                score: result.score
            })),
            images: (data.images || []).map((img: any) => {
                if (typeof img === "string") {
                    return { url: img, description: "" };
                }
                return {
                    url: img.url || "",
                    description: img.description || ""
                };
            })
        };
    } catch (error) {
        console.error("Hardcoded Tavily search error:", error);
        return null;
    }
}

export function VoiceModeOverlay({ isOpen, isDarkMode, onClose, onOpenPlus }: VoiceModeOverlayProps) {
    const { toast } = useToast();
    const isMobile = useIsMobile();

    type VoiceCard = {
        id: string;
        userText: string;
        aiText: string;
        isStreaming: boolean;
        aiAudioBlob?: Blob | null; // Changed from userAudioBlob to aiAudioBlob
        images?: Array<{ url: string; description?: string }>;
        sources?: Array<{ title: string; url: string; content?: string; favicon?: string }>;
        voiceModel?: string;
    };

    const [state, setState] = useState<VoiceModeState>("idle");
    const [errorText, setErrorText] = useState<string | null>(null);
    const [isSearchingWeb, setIsSearchingWeb] = useState(false);
    const [cards, setCards] = useState<VoiceCard[]>([]);
    const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
    const [activeCardIndex, setActiveCardIndex] = useState(0);
    const [currentTranscript, setCurrentTranscript] = useState<string>("");
    const [isPlaying, setIsPlaying] = useState(false);
    const [playingCardId, setPlayingCardId] = useState<string | null>(null);
    const [generatingCardId, setGeneratingCardId] = useState<string | null>(null);
    const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null);
    const [isCloseLoading, setIsCloseLoading] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [showMicPermissionGuide, setShowMicPermissionGuide] = useState(false);
    const [micGuideStep, setMicGuideStep] = useState(0);
    const silenceTimerRef = useRef<number | null>(null);
    const lastSpeechTimeRef = useRef<number>(Date.now());
    const [silenceCountdown, setSilenceCountdown] = useState<number | null>(null);
    const silenceCountdownIntervalRef = useRef<number | null>(null);
    const accumulatedTranscriptRef = useRef<string>("");
    const committedTranscriptRef = useRef<string>(""); // NEW: specific ref for committed final segments

    // Voice mode settings
    const [showSettingsMenu, setShowSettingsMenu] = useState(false);
    const [audioSpeed, setAudioSpeed] = useState<number>(() => {
        try {
            return parseFloat(localStorage.getItem('voice_mode_audio_speed') || '1');
        } catch { return 1; }
    });
    const [webSearchEnabled, setWebSearchEnabled] = useState<boolean>(() => {
        try {
            return localStorage.getItem('voice_mode_web_search') !== 'false';
        } catch { return true; }
    });
    const [reasoningLevel, setReasoningLevel] = useState<'medium' | 'high'>(() => {
        try {
            const saved = localStorage.getItem('voice_mode_reasoning');
            return saved === 'high' ? 'high' : 'medium';
        } catch { return 'medium'; }
    });

    const VOICE_MODEL_STORAGE_KEY = "voice_mode_voice_model_v1";
    const [isVoicePickerOpen, setIsVoicePickerOpen] = useState(false);
    // Move storage reading to a helper or effect if needed, but simple init is fine.
    // We'll update this state when the Settings page changes it via storage event if possible, 
    // or just rely on local storage being the source of truth on mount.
    const [selectedVoiceModel, setSelectedVoiceModel] = useState<string>(() => {
        try {
            return localStorage.getItem(VOICE_MODEL_STORAGE_KEY) || "aura-2-aries-en";
        } catch {
            return "aura-2-aries-en";
        }
    });

    const stateRef = useRef<VoiceModeState>(state);
    const isOpenRef = useRef<boolean>(isOpen);

    const cardsScrollRef = useRef<HTMLDivElement | null>(null);
    const isHandlingFinalRef = useRef(false);
    const lastFinalKeyRef = useRef<string>("");
    const isProgrammaticScrollRef = useRef(false);
    const lastNavTsRef = useRef<number>(0);

    const llmAbortRef = useRef<AbortController | null>(null);
    const manualTtsAbortRef = useRef<AbortController | null>(null);
    const ttsAbortRef = useRef<AbortController | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const previewAudioRef = useRef<HTMLAudioElement | null>(null);
    const previewAbortRef = useRef<AbortController | null>(null);
    const previewRequestIdRef = useRef(0);
    const connectingTimeoutRef = useRef<number | null>(null);

    const startRecordingRef = useRef<(() => Promise<void>) | null>(null);
    const handleSendTranscriptRef = useRef<((text: string) => Promise<void>) | null>(null);
    const hasInitForOpenRef = useRef(false);

    const CARDS_SESSION_KEY = "voice_mode_cards_v1";

    const lastUserAudioBlobRef = useRef<Blob | null>(null);
    const usedTapToSendRef = useRef(false);
    const transcriptRef = useRef<string>("");
    const tapFinalizeTimeoutRef = useRef<number | null>(null);
    const transcribingTimeoutRef = useRef<number | null>(null);

    // Touch tracking refs for card swiping
    const touchStartXRef = useRef<number>(0);
    const touchStartTimeRef = useRef<number>(0);
    const isSwipingRef = useRef<boolean>(false);
    const swipeDebounceRef = useRef<number | null>(null);

    const [shouldRender, setShouldRender] = useState<boolean>(isOpen);
    const [isClosing, setIsClosing] = useState<boolean>(false);
    const closeTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);

    useEffect(() => {
        try {
            localStorage.setItem(VOICE_MODEL_STORAGE_KEY, selectedVoiceModel);
        } catch {
        }
    }, [selectedVoiceModel]);

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(CARDS_SESSION_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw) as VoiceCard[];
            if (!Array.isArray(parsed)) return;
            setCards(parsed);
            setActiveCardIndex(Math.max(0, parsed.length - 1));
        } catch {
        }
    }, []);

    useEffect(() => {
        try {
            sessionStorage.setItem(CARDS_SESSION_KEY, JSON.stringify(cards));
        } catch {
        }
    }, [cards]);

    const statusText = useMemo(() => {
        if (state === "error") return errorText || "";
        if (state === "connecting" || isConnecting) return "Connecting";
        if (state === "recording") {
            // When recording, just show the hint text (not the transcript)
            return "Tap anywhere to send";
        }
        if (state === "thinking" || state === "transcribing") {
            return isSearchingWeb ? "Searching the web" : "Thinking";
        }
        if (state === "speaking") return "Tap to interrupt";
        return "Ready to go";
    }, [errorText, state, isConnecting, isSearchingWeb]);

    const stopAll = useCallback(() => {
        try {
            // This now only "disconnects" Deepgram but keeps the mic stream alive.
            deepgramService.disconnectDeepgram();
        } catch {
        }

        if (tapFinalizeTimeoutRef.current) {
            window.clearTimeout(tapFinalizeTimeoutRef.current);
            tapFinalizeTimeoutRef.current = null;
        }

        if (transcribingTimeoutRef.current) {
            window.clearTimeout(transcribingTimeoutRef.current);
            transcribingTimeoutRef.current = null;
        }

        if (llmAbortRef.current) {
            llmAbortRef.current.abort();
            llmAbortRef.current = null;
        }
        if (ttsAbortRef.current) {
            ttsAbortRef.current.abort();
            ttsAbortRef.current = null;
        }
        if (manualTtsAbortRef.current) {
            manualTtsAbortRef.current.abort();
            manualTtsAbortRef.current = null;
        }

        if (audioRef.current) {
            try {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            } catch {
            }
            audioRef.current = null;
        }

        if (previewAudioRef.current) {
            try {
                previewAudioRef.current.pause();
                previewAudioRef.current.currentTime = 0;
            } catch {
            }
            previewAudioRef.current = null;
        }

        if (audioPlayer) {
            try {
                audioPlayer.pause();
                audioPlayer.currentTime = 0;
            } catch { }
            setAudioPlayer(null);
        }
        setPlayingCardId(null);
        setIsPlaying(false);

        if (connectingTimeoutRef.current) {
            window.clearTimeout(connectingTimeoutRef.current);
            connectingTimeoutRef.current = null;
        }
        if (silenceTimerRef.current) {
            window.clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        if (silenceCountdownIntervalRef.current) {
            window.clearInterval(silenceCountdownIntervalRef.current);
            silenceCountdownIntervalRef.current = null;
        }
        setCurrentTranscript("");
        committedTranscriptRef.current = ""; // Clear committed transcript on stop
    }, []);

    // Ensure cleanup when opening/closing or on unmount
    useEffect(() => {
        if (!isOpen) {
            stopAll();
            deepgramService.dispose();
        }
        return () => {
            // Final exhaustive cleanup on unmount
            stopAll();
            deepgramService.dispose();
        };
    }, [isOpen, stopAll]);

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsClosing(false);
            if (closeTimeoutRef.current) {
                window.clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }
            return;
        }

        if (!shouldRender) return;
        setIsClosing(true);
        if (closeTimeoutRef.current) {
            window.clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = window.setTimeout(() => {
            setShouldRender(false);
            setIsClosing(false);
            closeTimeoutRef.current = null;
        }, 1000);
    }, [isOpen, shouldRender]);

    // Keep background scroll locked while the overlay is mounted (including during close animation).
    useEffect(() => {
        if (!shouldRender) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [shouldRender]);

    const interrupt = useCallback(() => {
        void startRecordingRef.current?.();
    }, []);

    const handleClose = useCallback(() => {
        setIsCloseLoading(true);

        // Close settings menu if open
        setShowSettingsMenu(false);

        // Play exit tone immediately
        try {
            const a = new Audio(VoiceModeExitTone);
            a.volume = 0.4;
            void a.play();
        } catch { }

        // Immediately stop all voice mode functions
        stopAll();
        deepgramService.dispose();

        // Clear silence timer
        if (silenceTimerRef.current) {
            window.clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }

        // Close immediately
        setIsCloseLoading(false);
        onClose();
    }, [onClose, stopAll]);

    const handleExportPDF = useCallback(() => {
        try {
            const doc = new jsPDF();
            let yPos = 20;
            const pageHeight = doc.internal.pageSize.height;
            const pageWidth = doc.internal.pageSize.width;
            const margin = 20;
            const maxWidth = pageWidth - (margin * 2);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);

            cards.forEach((card) => {
                // User Message - small normal font
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                doc.setTextColor(80, 80, 80);

                const userLines = doc.splitTextToSize(card.userText, maxWidth);
                const userLineHeight = 4;
                const userBlockHeight = userLines.length * userLineHeight;

                if (yPos + userBlockHeight > pageHeight - margin) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.text(userLines, margin, yPos);
                yPos += userBlockHeight + 3;

                // AI Message - slightly bigger serif font
                doc.setFont("times", "normal");
                doc.setFontSize(12);
                doc.setTextColor(0, 0, 0);

                const aiLines = doc.splitTextToSize(card.aiText, maxWidth);
                const aiLineHeight = 5;
                const aiBlockHeight = aiLines.length * aiLineHeight;

                if (yPos + aiBlockHeight > pageHeight - margin) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.text(aiLines, margin, yPos);
                yPos += aiBlockHeight + 10;

                if (yPos > pageHeight - margin) {
                    doc.addPage();
                    yPos = 20;
                }
            });

            doc.save(`voice-chat-${new Date().toISOString().slice(0, 10)}.pdf`);
            toast({
                title: "Exported",
                description: "",
            });
        } catch (e) {
            console.error(e);
            toast({
                title: "",
                description: "Failed to export PDF.",
                variant: "destructive",
            });
        }
    }, [cards, toast]);

    const shouldUseShimmer = useMemo(() => {
        // The user asked for no shimmer on the "we didn't catch that" / retry messages.
        const normalized = statusText.replace(/\s+/g, " ").trim().toLowerCase();
        if (state === "error") return false;
        if (normalized.includes("we didn't catch that")) return false;
        if (normalized.includes("can you try again")) return false;
        // Also show shimmer for "tap to interrupt"
        if (normalized.includes("tap to interrupt")) return true;
        // Also show shimmer for "tap anywhere to send"
        if (normalized.includes("tap anywhere")) return true;
        return true;
    }, [state, statusText]);

    const clampIndex = useCallback((idx: number) => {
        const max = Math.max(0, cards.length - 1);
        return Math.min(Math.max(0, idx), max);
    }, [cards.length]);

    useEffect(() => {
        if (!cards.length) return;
        const el = cardsScrollRef.current;
        if (!el) return;
        const width = el.clientWidth;
        if (!width) return;

        const targetLeft = clampIndex(activeCardIndex) * width;
        const currentLeft = el.scrollLeft;

        // If already at the correct position, skip
        if (Math.abs(currentLeft - targetLeft) < 5) return;

        isProgrammaticScrollRef.current = true;

        // Disable scroll-snap during programmatic scroll
        el.style.scrollSnapType = 'none';

        // Use scrollTo with instant behavior for button clicks
        el.scrollTo({ left: targetLeft, behavior: 'smooth' });

        // Re-enable scroll-snap after scroll completes
        window.setTimeout(() => {
            if (cardsScrollRef.current) {
                cardsScrollRef.current.style.scrollSnapType = 'x mandatory';
            }
            isProgrammaticScrollRef.current = false;
        }, 400);
    }, [activeCardIndex, cards.length, clampIndex]);

    const onTranscript = useCallback(
        async (result: TranscriptionResult) => {
            if (!result?.transcript) return;
            const currentState = stateRef.current;
            if (currentState !== "recording" && currentState !== "transcribing") return;

            // Update last speech time whenever we get transcript
            lastSpeechTimeRef.current = Date.now();

            // Clear any existing silence timer immediately (User is speaking or engine updated)
            if (silenceTimerRef.current) {
                window.clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = null;
            }

            if (result.isFinal) {
                // SCENARIO A: A final segment arrived (from Deepgram endpointing OR from our tap/silence auto-send).
                // Commit this chunk to our committed ref
                committedTranscriptRef.current = (committedTranscriptRef.current + " " + result.transcript).trim();
                // Sync accumulated to match committed (no dangling interim)
                accumulatedTranscriptRef.current = committedTranscriptRef.current;
                transcriptRef.current = committedTranscriptRef.current;

                // Clear any countdown state
                if (silenceCountdownIntervalRef.current) {
                    window.clearInterval(silenceCountdownIntervalRef.current);
                    silenceCountdownIntervalRef.current = null;
                }
                setSilenceCountdown(null);

                // Update UI with full committed text
                setCurrentTranscript(committedTranscriptRef.current);
            } else {
                // SCENARIO B: Interim results (User is likely still speaking).
                // Display = committed finals so far + current interim segment
                const displayText = (committedTranscriptRef.current + " " + result.transcript).trim();
                accumulatedTranscriptRef.current = displayText;
                transcriptRef.current = displayText;
                setCurrentTranscript(displayText);
            }

            // If we are in the finalizing "transcribing" state (waiting for timeout),
            // do not start silence timers or trigger immediate sending.
            if (currentState === "transcribing") {
                return;
            }

            // Set/reset silence timer for 3 seconds (enough time for natural pauses)
            silenceTimerRef.current = window.setTimeout(() => {
                const transcript = accumulatedTranscriptRef.current?.trim();

                // Safety check: Are we still recording? Is there text?
                if (stateRef.current === "recording" && transcript && !isHandlingFinalRef.current) {
                    // Auto-send after silence: directly trigger the send logic
                    usedTapToSendRef.current = true;

                    // Capture the final text to send
                    const finalToSend = transcript;

                    // Clear refs before sending to avoid duplication
                    committedTranscriptRef.current = "";
                    accumulatedTranscriptRef.current = "";
                    transcriptRef.current = "";

                    // Prevent duplicate handling
                    const finalKey = `${finalToSend}::auto`;
                    if (finalKey === lastFinalKeyRef.current) return;
                    lastFinalKeyRef.current = finalKey;
                    isHandlingFinalRef.current = true;

                    // Directly trigger the send pipeline (do NOT recursively call onTranscript)
                    void handleSendTranscriptRef.current?.(finalToSend);
                }
            }, 3000); // 3 seconds - gives more time for natural pauses

            if (!result.isFinal) {
                return;
            }

            // Only process final if user tapped to send (not auto-send from Deepgram endpointing)
            // Auto-send is now handled by our 3-second silence timer above
            if (!usedTapToSendRef.current) {
                // Don't auto-send from Deepgram's endpointing - wait for user tap or our silence timer
                return;
            }

            // If we are here, we want to SEND (user tapped).
            const textToSend = accumulatedTranscriptRef.current?.trim();
            if (!textToSend) return;

            // Prevent multiple finals from triggering duplicate sends / duplicate TTS.
            const sendKind = "tap";
            const finalKey = `${textToSend}::${sendKind}`;
            if (isHandlingFinalRef.current) return;
            if (finalKey === lastFinalKeyRef.current) return;
            lastFinalKeyRef.current = finalKey;
            isHandlingFinalRef.current = true;

            usedTapToSendRef.current = false;

            // Clear transcript refs before sending to avoid duplication
            committedTranscriptRef.current = "";
            accumulatedTranscriptRef.current = "";
            transcriptRef.current = "";

            void handleSendTranscriptRef.current?.(textToSend);
        },
        [cards.length, selectedVoiceModel, toast, webSearchEnabled]
    );

    // Extracted send pipeline: takes the final text and handles LLM + TTS
    const handleSendTranscript = useCallback(async (finalText: string) => {
        if (!finalText.trim()) {
            setState("error");
            setErrorText("we didn't catch that\ncan you try again");
            isHandlingFinalRef.current = false;
            return;
        }

        // Clear silence timer
        if (silenceTimerRef.current) {
            window.clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }

        // Stop any in-progress audio/TTS/LLM before starting a new run.
        if (llmAbortRef.current) {
            llmAbortRef.current.abort();
            llmAbortRef.current = null;
        }
        if (ttsAbortRef.current) {
            ttsAbortRef.current.abort();
            ttsAbortRef.current = null;
        }
        if (audioRef.current) {
            try {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            } catch {
            }
            audioRef.current = null;
        }
        if (audioPlayer) {
            try {
                audioPlayer.pause();
            } catch {
            }
            setAudioPlayer(null);
        }
        setPlayingCardId(null);
        setIsPlaying(false);

        setState("thinking");
        setErrorText(null);

        try {
            const blob = await deepgramService.stopRecordingAndGetBlob();
            lastUserAudioBlobRef.current = blob;
        } catch {
        }

        llmAbortRef.current = new AbortController();
        ttsAbortRef.current = new AbortController();

        const cardId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const currentUserBlob = lastUserAudioBlobRef.current; // Capture current blob

        // Insert user message immediately to the chat cards list
        setCards((prev) => {
            const next: VoiceCard[] = [
                ...prev,
                {
                    id: cardId,
                    userText: finalText,
                    aiText: "",
                    isStreaming: true,
                    aiAudioBlob: null,
                },
            ];
            // Jump to the newly created card
            setActiveCardIndex(next.length - 1);
            return next;
        });

        // Prepare memory: Last 5 cards as conversation history context (NOT as messages to answer)
        // These are for context only, the AI should not re-answer them
        const conversationHistory = cards.slice(-5).flatMap(c => {
            const u = c.userText;
            const a = c.aiText;
            return [
                { role: "user", content: u },
                { role: "assistant", content: a }
            ];
        });
        const currentMsg = finalText;

        let searchImages: Array<{ url: string; description?: string }> | undefined = undefined;
        let searchSources: Array<{ title: string; url: string }> | undefined = undefined;
        let finalMsgContent = currentMsg;
        let full = "";

        if (webSearchEnabled) {
            setIsSearchingWeb(true);
            try {
                const messages = [{ role: "user", content: currentMsg }];
                const decisionSystemPrompt = `You are a helpful voice assistant.
Decide if you need real-time info, weather, news, current events, or images to answer the user's question.
- If you DO need to search the web or get images, reply ONLY with: [SEARCH: <search query>]
- If you DO NOT need to search the web, simply answer the question directly. Keep responses concise and conversational (1-3 sentences). Do NOT use any markdown symbols.`;

                const firstResponse = await chatWithGroq({
                    messages,
                    signal: llmAbortRef.current.signal,
                    conversationHistory,
                    systemPromptOverride: decisionSystemPrompt
                });

                const searchMatch = firstResponse.match(/\[SEARCH:\s*(.*?)\]/i);
                if (searchMatch && searchMatch[1]) {
                    const searchQuery = searchMatch[1].trim();
                    const searchRes = await hardcodedTavilySearch(searchQuery);
                    if (searchRes) {
                        if (searchRes.results && searchRes.results.length > 0) {
                            const searchContext = `[Web Search Context]\nQuery: ${searchRes.query}\nSearch Results:\n${searchRes.results.slice(0, 5).map((r: any, i: number) => `${i + 1}. ${r.title}\nContent: ${r.content}`).join('\n')}\n\nTavily Direct Answer: ${searchRes.answer || ''}`;
                            finalMsgContent = `${currentMsg}\n\n${searchContext}`;
                            searchSources = searchRes.results.slice(0, 5).map((r: any) => ({
                                title: r.title,
                                url: r.url,
                                content: r.content,
                                favicon: r.favicon
                            }));
                        }
                        if (searchRes.images && searchRes.images.length > 0) {
                            searchImages = searchRes.images;
                        }
                        // Update the card with sources and images as soon as they are available
                        setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, images: searchImages, sources: searchSources } : c)));
                    }
                } else {
                    full = firstResponse;
                }
            } catch (searchErr) {
                console.error("Tavily search / decision error in voice mode:", searchErr);
            } finally {
                setIsSearchingWeb(false);
            }
        }

        try {
            if (!full) {
                // Only send the current message as the actual message to answer
                const messages = [{ role: "user", content: finalMsgContent }];
                full = await chatWithGroq({
                    messages,
                    signal: llmAbortRef.current.signal,
                    conversationHistory, // Pass history separately for context
                });
            }

            // Keep isStreaming true so the UI shows the streaming animation
            setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, aiText: full, isStreaming: true } : c)));

            // Turn off streaming after the animation completes (roughly 20ms per 3 chars)
            const estimatedStreamTime = Math.ceil(full.length / 3) * 20 + 500;
            setTimeout(() => {
                setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, isStreaming: false } : c)));
            }, estimatedStreamTime);

            setState("speaking");
            if (ttsAbortRef.current?.signal) {
                const sarvamSpeaker = mapVoiceToSarvamSpeaker(selectedVoiceModel);
                const { data: ttsData, error: ttsErr } = await supabase.functions.invoke('text-to-speech', {
                    body: {
                        text: sanitizeForTts(full),
                        provider: 'sarvam',
                        voiceId: sarvamSpeaker,
                        languageCode: 'en-IN',
                        pace: 1.2
                    }
                });

                if (ttsErr || !ttsData?.success || !ttsData?.audioContent) {
                    throw new Error(ttsErr?.message || ttsData?.error || "Sarvam TTS failed");
                }

                const binary = atob(ttsData.audioContent);
                const array = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    array[i] = binary.charCodeAt(i);
                }
                const blobAudio = new Blob([array], { type: 'audio/wav' });
                const url = URL.createObjectURL(blobAudio);
                const audio = new Audio(url);

                // Update card with audio blob and manage audio storage (keep last 2)
                setCards(prev => {
                    const updated = prev.map(c => c.id === cardId ? { ...c, aiAudioBlob: blobAudio, voiceModel: selectedVoiceModel } : c);
                    // Count how many cards have audio
                    const cardsWithAudio = updated.filter(c => c.aiAudioBlob).map(c => c.id);
                    if (cardsWithAudio.length > MAX_AUDIO_BLOBS) {
                        // Remove audio from oldest cards, keep only last MAX_AUDIO_BLOBS
                        const toKeep = new Set(cardsWithAudio.slice(-MAX_AUDIO_BLOBS));
                        return updated.map(c => toKeep.has(c.id) ? c : { ...c, aiAudioBlob: null });
                    }
                    return updated;
                });

                audioRef.current = audio;
                setAudioPlayer(audio);
                setPlayingCardId(cardId);
                setIsPlaying(true);

                try {
                    audio.playbackRate = audioSpeed;
                } catch { }
                await audio.play();
                await new Promise<void>((resolve) => {
                    audio.onended = () => {
                        setIsPlaying(false);
                        setPlayingCardId(null);
                        setAudioPlayer(null);
                        resolve();
                    };
                    audio.onerror = () => {
                        setIsPlaying(false);
                        setPlayingCardId(null);
                        setAudioPlayer(null);
                        resolve();
                    };
                });
                URL.revokeObjectURL(url);
            }
            if (isOpenRef.current && stateRef.current === "speaking") {
                void startRecordingRef.current?.();
            } else {
                if (stateRef.current === "speaking") {
                    setState("idle");
                }
            }
        } catch (e: any) {
            const msg = e?.message || "Voice request failed";
            toast({ title: "", description: msg, variant: "destructive" });
            setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, isStreaming: false } : c)));
            setState("error");
            setErrorText("we didn't catch that\ncan you try again");
        } finally {
            isHandlingFinalRef.current = false;
        }
    }, [cards, selectedVoiceModel, toast, webSearchEnabled, audioPlayer, audioSpeed]);

    useEffect(() => {
        handleSendTranscriptRef.current = handleSendTranscript;
    }, [handleSendTranscript]);

    useEffect(() => {
        if (audioPlayer) {
            try {
                audioPlayer.playbackRate = audioSpeed;
            } catch (err) {
                console.error("Failed to set playback rate:", err);
            }
        }
    }, [audioSpeed, audioPlayer]);

    const VOICES = useMemo(
        () => [
            {
                id: "aura-2-aries-en",
                title: "Aditya",
                subtitle: "Bold and direct",
                gradient: "conic-gradient(from 180deg, #f97316, #ef4444, #dc2626, #f97316)",
                glowColor: "rgba(239, 68, 68, 0.5)",
            },
            {
                id: "aura-2-pluto-en",
                title: "Rohan",
                subtitle: "Calm and steady",
                gradient: "conic-gradient(from 180deg, #6366f1, #8b5cf6, #a855f7, #6366f1)",
                glowColor: "rgba(139, 92, 246, 0.5)",
            },
            {
                id: "aura-2-selene-en",
                title: "Ritu",
                subtitle: "Bright and friendly",
                gradient: "conic-gradient(from 180deg, #10b981, #06b6d4, #22d3ee, #10b981)",
                glowColor: "rgba(16, 185, 129, 0.5)",
            },
            {
                id: "aura-2-vesta-en",
                title: "Priya",
                subtitle: "Warm and confident",
                gradient: "conic-gradient(from 180deg, #f59e0b, #fbbf24, #f97316, #f59e0b)",
                glowColor: "rgba(245, 158, 11, 0.5)",
            },
        ],
        []
    );

    const selectedVoiceIndex = useMemo(() => {
        const idx = VOICES.findIndex((v) => v.id === selectedVoiceModel);
        return idx >= 0 ? idx : 0;
    }, [VOICES, selectedVoiceModel]);

    const playVoicePreview = useCallback(
        async (model: string) => {
            try {
                previewRequestIdRef.current += 1;
                const requestId = previewRequestIdRef.current;

                if (previewAudioRef.current) {
                    try {
                        previewAudioRef.current.pause();
                        previewAudioRef.current.currentTime = 0;
                    } catch {
                    }
                    previewAudioRef.current = null;
                }

                if (previewAbortRef.current) {
                    previewAbortRef.current.abort();
                    previewAbortRef.current = null;
                }

                const sarvamSpeaker = mapVoiceToSarvamSpeaker(model);
                const { data: ttsData, error: ttsErr } = await supabase.functions.invoke('text-to-speech', {
                    body: {
                        text: "Hi! This is a voice preview.",
                        provider: 'sarvam',
                        voiceId: sarvamSpeaker,
                        languageCode: 'en-IN',
                        pace: 1.2
                    }
                });

                if (ttsErr || !ttsData?.success || !ttsData?.audioContent) {
                    throw new Error("Sarvam TTS preview failed");
                }

                const binary = atob(ttsData.audioContent);
                const array = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    array[i] = binary.charCodeAt(i);
                }
                const blob = new Blob([array], { type: 'audio/wav' });
                const url = URL.createObjectURL(blob);
                const audio = new Audio(url);

                if (requestId !== previewRequestIdRef.current) return;
                previewAudioRef.current = audio;
                await audio.play();
            } catch (e) {
                console.error("Preview failed:", e);
            }
        },
        []
    );

    const startRecording = useCallback(async () => {
        stopAll();
        transcriptRef.current = "";
        accumulatedTranscriptRef.current = "";
        committedTranscriptRef.current = ""; // Clear committed transcript for fresh start
        setCurrentTranscript("");
        setSilenceCountdown(null);
        lastUserAudioBlobRef.current = null;
        usedTapToSendRef.current = false;
        setState("recording");
        setErrorText(null);

        isHandlingFinalRef.current = false;
        lastFinalKeyRef.current = "";

        try {
            await deepgramService.startRecording(
                (r) => {
                    onTranscript(r);
                },
                (err) => {
                    toast({ title: "", description: err || "Could not start recording", variant: "destructive" });
                    setState("error");
                    setErrorText("we didn't catch that\ncan you try again");
                }
            );
        } catch (e: any) {
            toast({ title: "", description: e?.message || "Could not start recording", variant: "destructive" });
            setState("error");
            setErrorText("we didn't catch that\ncan you try again");
        }
    }, [onTranscript, stopAll, toast]);

    useEffect(() => {
        startRecordingRef.current = startRecording;
    }, [startRecording]);

    useEffect(() => {
        if (!isOpen) {
            hasInitForOpenRef.current = false;
            return;
        }
        if (hasInitForOpenRef.current) return;
        hasInitForOpenRef.current = true;

        // Always start fresh with a new session - clear previous cards
        setCards([]);
        setActiveCardIndex(0);
        sessionStorage.removeItem(CARDS_SESSION_KEY);
        setShowMicPermissionGuide(false);
        setMicGuideStep(0);

        setState("connecting");
        setIsConnecting(true);
        setErrorText(null);

        // Check microphone permission first
        const checkMicPermission = async () => {
            try {
                // Remove the 2-second lag and check permissions immediately with a tiny 100ms delay for animations
                await new Promise(resolve => setTimeout(resolve, 100));

                const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

                if (permissionStatus.state === 'denied') {
                    setIsConnecting(false);
                    setShowMicPermissionGuide(true);
                    setState("error");
                    setErrorText("Microphone access is blocked");
                    return;
                }

                // Try to get mic access to trigger permission prompt if 'prompt'
                await navigator.mediaDevices.getUserMedia({ audio: true });

                // Mic is available, continue with normal flow
                try {
                    const a = new Audio(VoiceModeStartTone);
                    a.volume = 0.3;
                    void a.play();
                } catch { }

                // Warm up and initialize Deepgram stream
                await deepgramService.initializeStream();

                setIsConnecting(false);
                void startRecordingRef.current?.();
            } catch (e) {
                // Mic permission denied or error
                setIsConnecting(false);
                setShowMicPermissionGuide(true);
                setState("error");
                setErrorText("Microphone access is required");
            }
        };

        checkMicPermission();

        return () => {
            stopAll();
            // We do NOT dispose here if we want to keep it warm, but usually on unmount (closing overlay) we should.
            // But handleClose does dispose. If the component unmounts for other reasons, we should dispose.
            // Since isOpen controls rendering, this unmounts when closed? No, it's conditionally rendered.
            // If the Overlay component itself unmounts (navigating away), we must dispose.
            deepgramService.dispose();
        };
    }, [isOpen, stopAll]);

    const stopRecording = useCallback(async (reason: "tap_anywhere" | "button") => {
        // Clear silence timer and countdown
        if (silenceTimerRef.current) {
            window.clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        if (silenceCountdownIntervalRef.current) {
            window.clearInterval(silenceCountdownIntervalRef.current);
            silenceCountdownIntervalRef.current = null;
        }
        setSilenceCountdown(null);

        if (reason === "tap_anywhere") {
            usedTapToSendRef.current = true;
            setState("transcribing");

            // Clear any existing transcribing/tap-finalize timeouts
            if (tapFinalizeTimeoutRef.current) {
                window.clearTimeout(tapFinalizeTimeoutRef.current);
            }

            // Wait 600ms for in-flight audio transcription to catch up from Deepgram
            tapFinalizeTimeoutRef.current = window.setTimeout(async () => {
                tapFinalizeTimeoutRef.current = null;
                const transcript = (accumulatedTranscriptRef.current || "").trim();

                if (transcript) {
                    // Clear all transcript refs before sending to avoid duplication
                    committedTranscriptRef.current = "";
                    accumulatedTranscriptRef.current = "";
                    transcriptRef.current = "";

                    // Prevent duplicate sends
                    const finalKey = `${transcript}::tap`;
                    if (isHandlingFinalRef.current) return;
                    if (finalKey === lastFinalKeyRef.current) return;
                    lastFinalKeyRef.current = finalKey;
                    isHandlingFinalRef.current = true;

                    // Directly trigger the send pipeline
                    void handleSendTranscript(transcript);
                } else {
                    // No transcript to send
                    setState("idle");
                    setErrorText(null);
                }

                // Also get the audio blob in parallel
                try {
                    const blob = await deepgramService.stopRecordingAndGetBlob();
                    lastUserAudioBlobRef.current = blob;
                } catch { }
            }, 600);
        } else {
            // Button stop = cancel - properly close WebSocket connection
            try {
                deepgramService.disconnectDeepgram();
            } catch { }
            // Reset all transcript refs
            committedTranscriptRef.current = "";
            accumulatedTranscriptRef.current = "";
            transcriptRef.current = "";
            setCurrentTranscript("");
            setState("idle");
            setErrorText(null);
        }
    }, [handleSendTranscript]);

    const scrollToCard = useCallback((idx: number) => {
        const el = cardsScrollRef.current;
        if (!el) return;
        const width = el.clientWidth;
        el.scrollTo({ left: idx * width, behavior: 'smooth' });
    }, []);

    const onCenterPress = useCallback(() => {
        // Record only while user explicitly toggles mic.
        if (state === "connecting") {
            if (connectingTimeoutRef.current) {
                window.clearTimeout(connectingTimeoutRef.current);
                connectingTimeoutRef.current = null;
            }
            void startRecording();
            return;
        }
        if (state === "idle" || state === "error") {
            startRecording();
            return;
        }
        if (state === "transcribing") {
            startRecording();
            return;
        }
        if (state === "recording") {
            void stopRecording("button");
            return;
        }
        // While thinking/speaking, keep tap-anywhere interrupt behavior.
        interrupt();
    }, [interrupt, startRecording, state, stopRecording]);


    const playAiAudio = useCallback(async (card: VoiceCard) => {
        const cardId = card.id;

        if (generatingCardId === cardId) {
            return;
        }

        if (playingCardId === cardId && isPlaying) {
            audioPlayer?.pause();
            setIsPlaying(false);
            return;
        }

        if (playingCardId === cardId && !isPlaying && audioPlayer) {
            try {
                await audioPlayer.play();
                setIsPlaying(true);
            } catch (err) {
                console.error("Failed to resume audio:", err);
            }
            return;
        }

        if (stateRef.current !== "idle" && stateRef.current !== "error") {
            stopAll();
            setState("idle");
        }

        if (audioPlayer) {
            audioPlayer.pause();
            setAudioPlayer(null);
            setIsPlaying(false);
            setPlayingCardId(null);
        }

        const hasValidBlob = card.aiAudioBlob && (card.aiAudioBlob instanceof Blob) && card.voiceModel === selectedVoiceModel;

        if (hasValidBlob) {
            const url = URL.createObjectURL(card.aiAudioBlob!);
            const a = new Audio(url);

            a.onended = () => {
                setIsPlaying(false);
                setPlayingCardId(null);
                URL.revokeObjectURL(url);
                setAudioPlayer(null);
            };
            a.onerror = () => {
                setIsPlaying(false);
                setPlayingCardId(null);
                URL.revokeObjectURL(url);
                setAudioPlayer(null);
            };

            setAudioPlayer(a);
            setPlayingCardId(cardId);
            setIsPlaying(true);
            try {
                a.playbackRate = audioSpeed;
                await a.play();
            } catch (err) {
                console.error("Failed to play audio:", err);
                setIsPlaying(false);
                setPlayingCardId(null);
                setAudioPlayer(null);
            }
        } else {
            setGeneratingCardId(cardId);

            if (manualTtsAbortRef.current) {
                manualTtsAbortRef.current.abort();
            }
            manualTtsAbortRef.current = new AbortController();

            try {
                const sarvamSpeaker = mapVoiceToSarvamSpeaker(selectedVoiceModel);
                const { data: ttsData, error: ttsErr } = await supabase.functions.invoke('text-to-speech', {
                    body: {
                        text: sanitizeForTts(card.aiText),
                        provider: 'sarvam',
                        voiceId: sarvamSpeaker,
                        languageCode: 'en-IN',
                        pace: 1.2
                    },
                    signal: manualTtsAbortRef.current.signal
                } as any);

                if (ttsErr || !ttsData?.success || !ttsData?.audioContent) {
                    throw new Error(ttsErr?.message || ttsData?.error || "TTS generation failed");
                }

                const binary = atob(ttsData.audioContent);
                const array = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    array[i] = binary.charCodeAt(i);
                }
                const blob = new Blob([array], { type: 'audio/wav' });

                setCards(prev => {
                    const updated = prev.map(c => c.id === cardId ? { ...c, aiAudioBlob: blob, voiceModel: selectedVoiceModel } : c);
                    const cardsWithAudio = updated.filter(c => c.aiAudioBlob).map(c => c.id);
                    if (cardsWithAudio.length > MAX_AUDIO_BLOBS) {
                        const toKeep = new Set(cardsWithAudio.slice(-MAX_AUDIO_BLOBS));
                        return updated.map(c => toKeep.has(c.id) ? c : { ...c, aiAudioBlob: null });
                    }
                    return updated;
                });

                setGeneratingCardId(null);

                const url = URL.createObjectURL(blob);
                const a = new Audio(url);

                a.onended = () => {
                    setIsPlaying(false);
                    setPlayingCardId(null);
                    URL.revokeObjectURL(url);
                    setAudioPlayer(null);
                };
                a.onerror = () => {
                    setIsPlaying(false);
                    setPlayingCardId(null);
                    URL.revokeObjectURL(url);
                    setAudioPlayer(null);
                };

                setAudioPlayer(a);
                setPlayingCardId(cardId);
                setIsPlaying(true);
                try {
                    a.playbackRate = audioSpeed;
                    await a.play();
                } catch (playErr) {
                    console.error("Failed to play generated audio:", playErr);
                    setIsPlaying(false);
                    setPlayingCardId(null);
                    setAudioPlayer(null);
                }
            } catch (e: any) {
                if (e?.name === 'AbortError') {
                    console.log("TTS generation aborted");
                    return;
                }
                console.error("Manual TTS generation failed:", e);
                toast({
                    title: "Playback Failed",
                    description: e?.message || "Could not generate audio for this response",
                    variant: "destructive"
                });
                setGeneratingCardId(null);
            }
        }
    }, [audioPlayer, isPlaying, playingCardId, selectedVoiceModel, toast, stopAll, generatingCardId, audioSpeed]);

    if (!shouldRender) return null;

    if (isVoicePickerOpen) {
        const current = VOICES[selectedVoiceIndex];
        return (
            <div
                className={
                    "fixed inset-0 z-[9999] flex flex-col overflow-hidden transition-opacity duration-1000 " +
                    (isClosing ? "opacity-0 pointer-events-none " : "opacity-100 ") +
                    (isDarkMode ? "bg-[#121212] text-white" : "bg-[#f7f7f8] text-black")
                }
                onClick={() => {
                }}
            >
                <div className="flex-1 flex flex-col items-center justify-center px-6">
                    <div className="text-center mb-8">
                        <div className="text-[18px] font-medium">Choose a voice</div>
                    </div>

                    <div className="relative w-[120px] h-[120px] flex items-center justify-center mb-6 overflow-visible">

                        {/* Outer Ambient Aura - Model specific */}
                        <div
                            className="absolute inset-[-25%] rounded-full blur-3xl opacity-40 animate-[aura-pulse_7s_ease-in-out_infinite] transition-all duration-700"
                            style={{
                                background: current.gradient,
                            }}
                        />

                        {/* Floating Glow Layer (organic motion) - Model specific */}
                        <div
                            className="absolute inset-[-18%] rounded-full blur-2xl opacity-40 animate-[float_9s_ease-in-out_infinite] transition-all duration-700"
                            style={{
                                background: `radial-gradient(circle at 30% 30%, ${current.glowColor}, transparent 65%)`,
                            }}
                        />

                        {/* Rotating Gradient Ring - Model specific */}
                        <div className="relative w-full h-full rounded-full overflow-hidden p-[2px]">
                            <div
                                className="absolute inset-[-60%] rounded-full blur-md opacity-80 animate-[slow-spin_14s_linear_infinite] transition-all duration-700"
                                style={{
                                    background: current.gradient,
                                }}
                            />

                            {/* Inner Mask */}
                            <div
                                className={`absolute inset-[6px] rounded-full transition-colors duration-700 ${isDarkMode ? "bg-[#121212]" : "bg-[#f7f7f8]"
                                    }`}
                            />
                        </div>
                    </div>


                    <div className="flex items-center gap-6 mb-8">
                        <button
                            type="button"
                            onClick={() => {
                                const next = (selectedVoiceIndex - 1 + VOICES.length) % VOICES.length;
                                const v = VOICES[next];
                                setSelectedVoiceModel(v.id);
                                void playVoicePreview(v.id);
                            }}
                            className={"px-2 py-2 " + (isDarkMode ? "text-white/80" : "text-black/70")}
                        >
                            <FontAwesomeIcon icon={faChevronDown} rotation={90} />
                        </button>

                        <div className="text-center min-w-[220px]">
                            <div className="text-[16px] font-semibold">{current?.title}</div>
                            <div className={"text-[12px] mt-0.5 " + (isDarkMode ? "text-white/60" : "text-black/50")}>{current?.subtitle}</div>
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                const next = (selectedVoiceIndex + 1) % VOICES.length;
                                const v = VOICES[next];
                                setSelectedVoiceModel(v.id);
                                void playVoicePreview(v.id);
                            }}
                            className={"px-2 py-2 " + (isDarkMode ? "text-white/80" : "text-black/70")}
                        >
                            <FontAwesomeIcon icon={faChevronDown} rotation={270} />
                        </button>
                    </div>

                    <div className="flex flex-col items-center gap-3">
                        <Button
                            type="button"
                            onClick={() => {
                                // Stop any playing preview audio
                                if (previewAudioRef.current) {
                                    try {
                                        previewAudioRef.current.pause();
                                        previewAudioRef.current.currentTime = 0;
                                    } catch { }
                                    previewAudioRef.current = null;
                                }
                                setIsVoicePickerOpen(false);
                            }}
                            className={
                                "min-w-[220px] rounded-full " +
                                (isDarkMode ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-black/90")
                            }
                        >
                            Done
                        </Button>
                        <button
                            type="button"
                            onClick={() => {
                                // Stop any playing preview audio
                                if (previewAudioRef.current) {
                                    try {
                                        previewAudioRef.current.pause();
                                        previewAudioRef.current.currentTime = 0;
                                    } catch { }
                                    previewAudioRef.current = null;
                                }
                                setIsVoicePickerOpen(false);
                            }}
                            className={"text-[13px] " + (isDarkMode ? "text-white/70" : "text-black/60")}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Show microphone permission guide carousel
    if (showMicPermissionGuide) {
        const instruction = MIC_ENABLE_INSTRUCTIONS[micGuideStep];
        return (
            <div
                className={
                    "fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden transition-opacity duration-1000 " +
                    (isClosing ? "opacity-0 pointer-events-none " : "opacity-100 ") +
                    (isDarkMode ? "bg-[#121212] text-white" : "bg-[#f7f7f8] text-black")
                }
            >
                <div className="max-w-md w-full px-6">
                    <div className="text-center mb-8">
                        <div className={`text-6xl mb-4 ${isDarkMode ? 'text-white/80' : 'text-black/80'}`}>🎤</div>
                        <h2 className="text-xl font-semibold mb-2">Enable Microphone</h2>
                        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-black/60'}`}>
                            Voice mode needs microphone access to work
                        </p>
                    </div>

                    <div className={
                        "rounded-2xl p-6 mb-6 " +
                        (isDarkMode ? "bg-white/5 border border-white/10" : "bg-black/5 border border-black/10")
                    }>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <span className="text-3xl">{instruction.icon}</span>
                            </div>
                        </div>
                        <h3 className="text-lg font-medium mb-2">{instruction.title}</h3>
                        <p className={isDarkMode ? "text-white/70" : "text-black/70"}>
                            {instruction.description}
                        </p>
                    </div>

                    <div className="flex items-center justify-center gap-4 mb-6">
                        <button
                            onClick={() => setMicGuideStep(prev => Math.max(0, prev - 1))}
                            disabled={micGuideStep === 0}
                            className={
                                "h-10 w-10 rounded-full flex items-center justify-center transition-colors " +
                                (micGuideStep === 0
                                    ? "opacity-30 cursor-not-allowed"
                                    : isDarkMode ? "hover:bg-white/10" : "hover:bg-black/10")
                            }
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>

                        <div className="flex gap-2">
                            {MIC_ENABLE_INSTRUCTIONS.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setMicGuideStep(idx)}
                                    className={
                                        "h-2 w-2 rounded-full transition-all " +
                                        (idx === micGuideStep
                                            ? isDarkMode ? "bg-white" : "bg-black"
                                            : isDarkMode ? "bg-white/30" : "bg-black/30")
                                    }
                                />
                            ))}
                        </div>

                        <button
                            onClick={() => setMicGuideStep(prev => Math.min(MIC_ENABLE_INSTRUCTIONS.length - 1, prev + 1))}
                            disabled={micGuideStep === MIC_ENABLE_INSTRUCTIONS.length - 1}
                            className={
                                "h-10 w-10 rounded-full flex items-center justify-center transition-colors " +
                                (micGuideStep === MIC_ENABLE_INSTRUCTIONS.length - 1
                                    ? "opacity-30 cursor-not-allowed"
                                    : isDarkMode ? "hover:bg-white/10" : "hover:bg-black/10")
                            }
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                    <Button
                        onClick={handleClose}
                        className={
                            "w-full rounded-full " +
                            (isDarkMode ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-black/90")
                        }
                    >
                        Close
                    </Button>
                </div>
            </div>
        );
    }
    return (
        <div
            className={
                "fixed inset-0 z-[9999] flex flex-col overflow-hidden transition-opacity duration-1000 " +
                (isClosing ? "opacity-0 pointer-events-none " : "opacity-100 ") +
                (isDarkMode ? "bg-[#121212] text-white" : "bg-[#f7f7f8] text-black")
            }
            style={{ overscrollBehaviorX: 'contain' }}
            onClick={() => {
                // Tap-to-send during recording, tap-to-interrupt during speaking
                if (state === "recording") {
                    void stopRecording("tap_anywhere");
                } else if (state === "speaking") {
                    interrupt();
                }
            }}
        >
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsVoicePickerOpen(true);
                    void playVoicePreview(selectedVoiceModel);
                }}
                className={`fixed top-4 right-4 z-[10001] h-9 w-9 rounded-full backdrop-blur flex items-center justify-center
                    ${isDarkMode ? "hover:bg-white/10 text-white" : "hover:bg-black/5 text-black"}`}
                aria-label="Voice settings"
            >
                <Settings2 className="text-6" />
            </button>

            <div className="relative flex-1 px-4 sm:px-6 pt-6 sm:pt-10 pb-32 sm:pb-40 flex flex-col min-h-0">
                <div className="max-w-3xl mx-auto flex-1 min-h-0 w-full relative flex flex-col">
                    <div className="relative flex-1 min-h-0 overflow-hidden rounded-3xl flex flex-col">
                        {cards.length ? (
                            <div
                                ref={cardsScrollRef}
                                className="flex-1 min-h-0 w-full overflow-x-auto overflow-y-hidden flex snap-x snap-mandatory scrollbar-hide"
                                style={{
                                    WebkitOverflowScrolling: 'touch',
                                    scrollSnapType: 'x mandatory',
                                    scrollBehavior: 'smooth',
                                    overscrollBehaviorX: 'contain'
                                } as any}
                                onTouchStart={(e) => {
                                    touchStartXRef.current = e.touches[0].clientX;
                                    touchStartTimeRef.current = Date.now();
                                    isSwipingRef.current = false;
                                    // Clear any pending debounce
                                    if (swipeDebounceRef.current) {
                                        window.clearTimeout(swipeDebounceRef.current);
                                        swipeDebounceRef.current = null;
                                    }
                                }}
                                onTouchMove={(e) => {
                                    if (!touchStartXRef.current) return;
                                    const deltaX = Math.abs(e.touches[0].clientX - touchStartXRef.current);
                                    // Mark as swiping if moved more than 10px
                                    if (deltaX > 10) {
                                        isSwipingRef.current = true;
                                    }
                                }}
                                onTouchEnd={(e) => {
                                    const touchDuration = Date.now() - touchStartTimeRef.current;
                                    const touchEndX = e.changedTouches[0].clientX;
                                    const deltaX = touchEndX - touchStartXRef.current;

                                    // Debounce scroll index update to prevent double swipe
                                    swipeDebounceRef.current = window.setTimeout(() => {
                                        const el = cardsScrollRef.current;
                                        if (!el) return;
                                        const width = el.clientWidth || 1;
                                        const idx = Math.round(el.scrollLeft / width);
                                        setActiveCardIndex(clampIndex(idx));
                                        swipeDebounceRef.current = null;
                                    }, 100);

                                    touchStartXRef.current = 0;
                                    touchStartTimeRef.current = 0;
                                }}
                                onScroll={() => {
                                    // Skip if programmatic scroll or touch in progress
                                    if (isProgrammaticScrollRef.current) return;
                                    if (touchStartTimeRef.current > 0) return;
                                    if (swipeDebounceRef.current) return;

                                    // Skip if a navigation button was clicked recently
                                    const timeSinceNav = Date.now() - lastNavTsRef.current;
                                    if (timeSinceNav < 500) return;

                                    const el = cardsScrollRef.current;
                                    if (!el) return;
                                    const width = el.clientWidth || 1;
                                    const idx = Math.round(el.scrollLeft / width);
                                    setActiveCardIndex(clampIndex(idx));
                                }}
                            >
                                {cards.map((c) => (
                                    <div
                                        key={c.id}
                                        className="h-full min-h-0 overflow-hidden w-full flex-shrink-0 snap-center snap-stop-always flex flex-col"
                                    >
                                        <div
                                            className={
                                                "flex-1 min-h-0 rounded-3xl shadow-sm px-6 sm:px-8 py-7 overflow-y-auto overscroll-contain flex flex-col pointer-events-auto"}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div
                                                className={
                                                    "mb-5 text-[13px] sm:text-[14px] leading-relaxed text-left " +
                                                    (isDarkMode ? "text-white/70" : "text-black/60")
                                                }
                                            >
                                                <span>
                                                    {c.userText}
                                                </span>
                                            </div>
                                            <div className="text-[18px] sm:text-[16px] leading-[1.35] font-sans tracking-tight flex-1 text-left">
                                                <StreamingCardContent
                                                    content={c.aiText}
                                                    isStreaming={c.isStreaming}
                                                    isDarkMode={isDarkMode}
                                                    maxLength={10000}
                                                />
                                                {!c.isStreaming && (
                                                    <div className="inline-flex items-center border border-[1px] rounded-2xl">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                playAiAudio(c);
                                                            }}
                                                            className={`group relative inline-flex h-7 w-7 items-center justify-center rounded-l-full transition-all duration-200 ${isDarkMode
                                                                    ? "bg-white/10 text-white hover:bg-white/20"
                                                                    : "bg-black/10 text-black hover:bg-black/20"
                                                                }`}
                                                            title={playingCardId === c.id && isPlaying ? "Pause" : "Play audio"}
                                                        >
                                                            {generatingCardId === c.id ? (
                                                                <FontAwesomeIcon
                                                                    icon={faSpinner}
                                                                    spin
                                                                    className="text-[11px] opacity-80"
                                                                />
                                                            ) : (
                                                                <FontAwesomeIcon
                                                                    icon={(playingCardId === c.id && isPlaying) ? faPause : faPlay}
                                                                    className="text-[11px] ml-[1px]"
                                                                />
                                                            )}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();

                                                                const speeds = [1, 1.25, 1.5, 2];
                                                                const nextSpeed =
                                                                    speeds[(speeds.indexOf(audioSpeed) + 1) % speeds.length];

                                                                setAudioSpeed(nextSpeed);

                                                                try {
                                                                    localStorage.setItem(
                                                                        "voice_mode_audio_speed",
                                                                        nextSpeed.toString()
                                                                    );
                                                                } catch { }
                                                            }}
                                                            className={`inline-flex h-7 min-w-[34px] items-center justify-center rounded-r-full px-2 text-[10px] font-semibold tracking-tight transition-all duration-200 ${isDarkMode
                                                                    ? "bg-white/10 text-white hover:bg-white/20"
                                                                    : "bg-black/10 text-black hover:bg-black/20"
                                                                }`}
                                                            title="Change playback speed"
                                                        >
                                                            {audioSpeed}x
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Images Carousel */}
                                                {c.images && c.images.length > 0 && !c.isStreaming && (
                                                    <div className="mt-6 w-full" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex gap-4 overflow-x-auto pb-3 pt-1 scrollbar-hide snap-x snap-mandatory">
                                                            {c.images.map((img, imgIdx) => (
                                                                <div
                                                                    key={imgIdx}
                                                                    className="flex-shrink-0 w-52 h-36 rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 snap-start relative group shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setFullScreenImage(img.url);
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={img.url}
                                                                        alt={img.description || `Search image ${imgIdx + 1}`}
                                                                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                                                                        loading="lazy"
                                                                        onError={(e) => {
                                                                            (e.target as HTMLElement).style.display = 'none';
                                                                        }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Sources links */}
                                                {c.sources && c.sources.length > 0 && !c.isStreaming && (
                                                    <div className="mt-6 pt-3 border-t border-black/10 dark:border-white/10 w-full" onClick={(e) => e.stopPropagation()}>
                                                        <div className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 mb-2.5 uppercase tracking-wider">
                                                            Sources
                                                        </div>
                                                        <div className="flex flex-row gap-2 flex-wrap">
                                                            {c.sources.map((src, idx) => {
                                                                const displaySnippet = src.content
                                                                    ? (src.content.length > 40 ? src.content.substring(0, 40) + '...' : src.content)
                                                                    : (src.title || getDisplayUrl(src.url));
                                                                return (
                                                                    <a
                                                                        key={idx}
                                                                        href={src.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="flex items-center gap-2.5 p-2 w-[max-content] max-w-[200px] rounded-xl border border-black/5 dark:border-white/5 bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-neutral-800 dark:text-neutral-200 transition-colors"
                                                                    >
                                                                        {src.favicon ? (
                                                                            <img
                                                                                src={src.favicon}
                                                                                alt=""
                                                                                className="w-6 h-6 rounded-sm flex-shrink-0"
                                                                                onError={(e) => {
                                                                                    (e.target as HTMLElement).style.display = 'none';
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <ExternalLink className="w-3.5 h-3.5 opacity-60 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                                                                        )}
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-[11px] leading-snug dark:text-neutral-300 text-neutral-700 font-normal line-clamp-1">
                                                                                {displaySnippet}
                                                                            </p>
                                                                            <span className="text-[9px] opacity-50 block truncate">
                                                                                {getDisplayUrl(src.url)}
                                                                            </span>
                                                                        </div>
                                                                    </a>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex-1 w-full flex items-center justify-center">
                                <div className={`${isDarkMode ? "text-white/60" : "text-black/60"} select-none text-center`}>Whenever you're ready</div>
                            </div>
                        )}

                        {/* Bottom fade effect */}
                        {cards.length > 0 && (
                            <div
                                className={
                                    "absolute bottom-0 left-0 right-0 h-16 pointer-events-none z-10 " +
                                    (isDarkMode
                                        ? "bg-gradient-to-t from-[#121212] to-transparent"
                                        : "bg-gradient-to-t from-[#f7f7f8] to-transparent"
                                    )
                                }
                            />
                        )}
                    </div>

                    {
                        cards.length > 1 ? (
                            <div
                                className="fixed right-4 sm:right-6 top-1/2 -translate-y-1/2 flex flex-col items-center gap-0 z-[10002]"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        lastNavTsRef.current = Date.now();
                                        const next = clampIndex(activeCardIndex - 1);
                                        setActiveCardIndex(next);
                                    }}
                                    className={
                                        "h-8 w-8 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 active:scale-90 transition-transform " +
                                        (isDarkMode ? "text-white/70 hover:text-white" : "text-black/50 hover:text-black")
                                    }
                                >
                                    <FontAwesomeIcon icon={faChevronDown} rotation={180} className="text-lg" />
                                </button>

                                <div className="flex flex-col items-center gap-2 py-2">
                                    {cards.map((c, idx) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                lastNavTsRef.current = Date.now();
                                                setActiveCardIndex(idx);
                                            }}
                                            className={
                                                "h-2 w-2 rounded-full transition-all cursor-pointer " +
                                                (idx === activeCardIndex
                                                    ? isDarkMode
                                                        ? "bg-white opacity-100 scale-125"
                                                        : "bg-black opacity-100 scale-125"
                                                    : isDarkMode
                                                        ? "bg-white opacity-30 hover:opacity-60"
                                                        : "bg-black opacity-30 hover:opacity-60")
                                            }
                                            aria-label={`Go to response ${idx + 1}`}
                                        />
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        lastNavTsRef.current = Date.now();
                                        const next = clampIndex(activeCardIndex + 1);
                                        setActiveCardIndex(next);
                                    }}
                                    className={
                                        "h-8 w-8 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 active:scale-90 transition-transform " +
                                        (isDarkMode ? "text-white/70 hover:text-white" : "text-black/50 hover:text-black")
                                    }
                                >
                                    <FontAwesomeIcon icon={faChevronDown} className="text-lg" />
                                </button>
                            </div>
                        ) : null
                    }
                </div>
            </div>



            <div className="absolute inset-x-0 bottom-0 z-[10000]">
                <div className="px-5 sm:px-6 pb-5">
                    <div className="max-w-3xl mx-auto">
                        <div className="mb-3 relative bottom-20">
                            {/* Shimmer status text (Tap anywhere to send / Tap to interrupt) */}
                            <div className="text-center" style={{ ['--shimmer-duration' as any]: '2.6s' }}>
                                {shouldUseShimmer ? (
                                    <ShimmerText text={statusText} isDarkMode={isDarkMode} className="text-[0.95rem] text-center whitespace-pre-line" />
                                ) : (
                                    <div className={"text-[0.95rem] text-center whitespace-pre-line select-none " + (isDarkMode ? "text-white/80" : "text-black/80")}>
                                        {statusText}
                                    </div>
                                )}
                            </div>

                            {/* Transcription text displayed as normal text (shown during recording when there's text) */}
                            {state === "recording" && currentTranscript && (
                                <div className="mt-2 px-4">
                                    <div
                                        className={
                                            "text-[0.9rem] text-center select-none overflow-hidden " +
                                            (isDarkMode ? "text-white/70" : "text-black/60")
                                        }
                                        style={{
                                            maxWidth: '100vw',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {currentTranscript}
                                    </div>
                                </div>
                            )}
                        </div>



                        <div className="relative">
                            <div
                                className={
                                    "voice-controls mx-auto absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center justify-center max-w-[190px] gap-3 px-1.5 py-1.5 rounded-[999px] border backdrop-blur " +
                                    (isDarkMode ? "bg-white/5 border-white/10" : "bg-white/70 border-black/10")
                                }

                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Settings Button */}
                                <div className="relative">
                                    <Button
                                        type="button"
                                        onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                                        className={
                                            "voice-control-btn " +
                                            (isDarkMode
                                                ? "bg-white/10 hover:bg-white/15 border-white/10 text-white shadow-[0_0_15px_rgba(255,255,255,0.2)]"
                                                : "bg-black/5 hover:bg-black/10 border-black/10 text-black shadow-[0_10px_28px_rgba(0,0,0,0.5)]")
                                        }
                                    >
                                        <FontAwesomeIcon icon={faEllipsisVertical} className="text-xl" />
                                    </Button>

                                    {/* Settings Menu (Desktop - sm and above) */}
                                    {showSettingsMenu && (
                                        <div
                                            className={
                                                "hidden sm:block absolute bottom-full -left-6 mb-3 w-56 rounded-2xl p-3 shadow-xl border z-50 " +
                                                (isDarkMode ? "bg-[#1a1a1a] border-white/10" : "bg-white border-gray-200")
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {/* Audio Speed */}
                                            {/* <div className="mb-4">
                                                <div className={"text-xs font-medium mb-2 " + (isDarkMode ? "text-white/60" : "text-gray-600")}>
                                                    Audio Speed
                                                </div>
                                                <div className="flex gap-1">
                                                    {[1, 1.25, 1.5, 1.75].map((speed) => (
                                                        <button
                                                            key={speed}
                                                            onClick={() => {
                                                                setAudioSpeed(speed);
                                                                localStorage.setItem('voice_mode_audio_speed', speed.toString());
                                                            }}
                                                            className={
                                                                "flex-1 py-1.5 text-xs rounded-lg transition-colors " +
                                                                (audioSpeed === speed
                                                                    ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                                    : isDarkMode ? "bg-white/10 text-white hover:bg-white/20" : "bg-gray-100 text-gray-900 hover:bg-gray-200")
                                                            }
                                                        >
                                                            {speed}x
                                                        </button>
                                                    ))}
                                                </div>
                                            </div> */}

                                            {/* Web Search */}
                                            <div className="mb-4">
                                                <div className={"text-xs font-medium mb-2 " + (isDarkMode ? "text-white/60" : "text-gray-600")}>
                                                    Web Search
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setWebSearchEnabled(true);
                                                            localStorage.setItem('voice_mode_web_search', 'true');
                                                        }}
                                                        className={
                                                            "flex-1 py-1.5 text-xs rounded-lg transition-colors " +
                                                            (webSearchEnabled
                                                                ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                                : isDarkMode ? "bg-white/10 text-white hover:bg-white/20" : "bg-gray-100 text-gray-900 hover:bg-gray-200")
                                                        }
                                                    >
                                                        On
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setWebSearchEnabled(false);
                                                            localStorage.setItem('voice_mode_web_search', 'false');
                                                        }}
                                                        className={
                                                            "flex-1 py-1.5 text-xs rounded-lg transition-colors " +
                                                            (!webSearchEnabled
                                                                ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                                : isDarkMode ? "bg-white/10 text-white hover:bg-white/20" : "bg-gray-100 text-gray-900 hover:bg-gray-200")
                                                        }
                                                    >
                                                        Off
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Reasoning */}
                                            <div>
                                                <div className={"text-xs font-medium mb-2 " + (isDarkMode ? "text-white/60" : "text-gray-600")}>
                                                    Reasoning
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setReasoningLevel('medium');
                                                            localStorage.setItem('voice_mode_reasoning', 'medium');
                                                        }}
                                                        className={
                                                            "flex-1 py-1.5 text-xs rounded-lg transition-colors " +
                                                            (reasoningLevel === 'medium'
                                                                ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                                : isDarkMode ? "bg-white/10 text-white hover:bg-white/20" : "bg-gray-100 text-gray-900 hover:bg-gray-200")
                                                        }
                                                    >
                                                        Medium
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setReasoningLevel('high');
                                                            localStorage.setItem('voice_mode_reasoning', 'high');
                                                        }}
                                                        className={
                                                            "flex-1 py-1.5 text-xs rounded-lg transition-colors " +
                                                            (reasoningLevel === 'high'
                                                                ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                                : isDarkMode ? "bg-white/10 text-white hover:bg-white/20" : "bg-gray-100 text-gray-900 hover:bg-gray-200")
                                                        }
                                                    >
                                                        High
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Export PDF */}
                                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-white/10">
                                                <button
                                                    onClick={handleExportPDF}
                                                    className={
                                                        "w-full py-1.5 text-xs rounded-lg transition-colors flex items-center justify-center gap-2 " +
                                                        (isDarkMode ? "bg-white/10 text-white hover:bg-white/20" : "bg-gray-100 text-gray-900 hover:bg-gray-200")
                                                    }
                                                >
                                                    <span>Export Chat to PDF</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Animated Mic/Stop Button */}
                                <div
                                    className="relative cursor-pointer"
                                    onClick={() => onCenterPress()}
                                >
                                    {state === "recording" ? (
                                        /* Plasma Stop Button - Only when recording */
                                        <PlasmaStopButton isDarkMode={isDarkMode} />
                                    ) : (
                                        /* Simple Mic Button - When not recording */
                                        <div
                                            className={`voice-control-btn relative w-[52px] h-[52px] rounded-full border flex justify-center items-center ${isDarkMode
                                                ? "bg-white/10 hover:bg-white/15 border-white/10 text-white"
                                                : "bg-black/5 hover:bg-black/10 border-black/10 text-black"
                                                }`}>
                                            <FontAwesomeIcon
                                                icon={faMicrophone}
                                                className={`text-xl ${isDarkMode ? "text-white" : "text-black"}`}
                                            />
                                        </div>
                                    )}
                                </div>

                                <Button
                                    type="button"
                                    onClick={() => {
                                        handleClose();
                                    }}
                                    className={
                                        "voice-control-btn " +
                                        (isDarkMode
                                            ? "bg-white/10 hover:bg-white/15 border-white/10 text-white shadow-[0_0_15px_rgba(255,255,255,0.2)]"
                                            : "bg-black/5 hover:bg-black/10 border-black/10 text-black shadow-[0_10px_28px_rgba(0,0,0,0.5)]")
                                    }
                                >
                                    {isCloseLoading ? (
                                        <FontAwesomeIcon icon={faSpinner} spin className="text-xl" />
                                    ) : (
                                        <FontAwesomeIcon icon={faXmark} className="text-xl" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Settings Drawer (Bottom Sheet) - Only on screens < sm */}
            {isMobile && (
                <Drawer
                    open={showSettingsMenu}
                    onOpenChange={(open) => {
                        if (!open) {
                            setShowSettingsMenu(false);
                        }
                    }}
                >
                    <DrawerContent
                        className={
                            "mx-auto flex w-full max-w-lg flex-col rounded-t-3xl border pb-6 shadow-2xl backdrop-blur-lg " +
                            (isDarkMode ? "border-white/10 bg-[#1a1a1a]/95" : "border-gray-200 bg-white/95")
                        }
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-5 pt-3">
                            {/* Title */}
                            <div className={`text-sm font-semibold mb-4 ${isDarkMode ? "text-white" : "text-black"}`}>
                                Voice Settings
                            </div>

                            {/* Audio Speed */}
                            {/* <div className="mb-5">
                                <div className={"text-xs font-medium mb-2 " + (isDarkMode ? "text-white/60" : "text-gray-600")}>
                                    Audio Speed
                                </div>
                                <div className="flex gap-2">
                                    {[1, 1.25, 1.5, 1.75].map((speed) => (
                                        <button
                                            key={speed}
                                            onClick={() => {
                                                setAudioSpeed(speed);
                                                localStorage.setItem('voice_mode_audio_speed', speed.toString());
                                            }}
                                            className={
                                                "flex-1 py-2.5 text-sm rounded-xl transition-colors " +
                                                (audioSpeed === speed
                                                    ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                    : isDarkMode ? "bg-white/10 text-white" : "bg-gray-100 text-gray-900")
                                            }
                                        >
                                            {speed}x
                                        </button>
                                    ))}
                                </div>
                            </div> */}

                            {/* Web Search */}
                            <div className="mb-5">
                                <div className={"text-xs font-medium mb-2 " + (isDarkMode ? "text-white/60" : "text-gray-600")}>
                                    Web Search
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            setWebSearchEnabled(true);
                                            localStorage.setItem('voice_mode_web_search', 'true');
                                        }}
                                        className={
                                            "flex-1 py-2.5 text-sm rounded-xl transition-colors " +
                                            (webSearchEnabled
                                                ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                : isDarkMode ? "bg-white/10 text-white" : "bg-gray-100 text-gray-900")
                                        }
                                    >
                                        On
                                    </button>
                                    <button
                                        onClick={() => {
                                            setWebSearchEnabled(false);
                                            localStorage.setItem('voice_mode_web_search', 'false');
                                        }}
                                        className={
                                            "flex-1 py-2.5 text-sm rounded-xl transition-colors " +
                                            (!webSearchEnabled
                                                ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                : isDarkMode ? "bg-white/10 text-white" : "bg-gray-100 text-gray-900")
                                        }
                                    >
                                        Off
                                    </button>
                                </div>
                            </div>

                            {/* Reasoning */}
                            <div className="mb-2">
                                <div className={"text-xs font-medium mb-2 " + (isDarkMode ? "text-white/60" : "text-gray-600")}>
                                    Reasoning
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            setReasoningLevel('medium');
                                            localStorage.setItem('voice_mode_reasoning', 'medium');
                                        }}
                                        className={
                                            "flex-1 py-2.5 text-sm rounded-xl transition-colors " +
                                            (reasoningLevel === 'medium'
                                                ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                : isDarkMode ? "bg-white/10 text-white" : "bg-gray-100 text-gray-900")
                                        }
                                    >
                                        Medium
                                    </button>
                                    <button
                                        onClick={() => {
                                            setReasoningLevel('high');
                                            localStorage.setItem('voice_mode_reasoning', 'high');
                                        }}
                                        className={
                                            "flex-1 py-2.5 text-sm rounded-xl transition-colors " +
                                            (reasoningLevel === 'high'
                                                ? isDarkMode ? "bg-white text-black" : "bg-black text-white"
                                                : isDarkMode ? "bg-white/10 text-white" : "bg-gray-100 text-gray-900")
                                        }
                                    >
                                        High
                                    </button>
                                </div>
                            </div>

                            {/* Export PDF */}
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
                                <button
                                    onClick={handleExportPDF}
                                    className={
                                        "w-full py-3 text-sm rounded-xl transition-colors font-medium " +
                                        (isDarkMode ? "bg-white/10 text-white hover:bg-white/20" : "bg-gray-100 text-gray-900 hover:bg-gray-200")
                                    }
                                >
                                    Export Chat to PDF
                                </button>
                            </div>
                        </div>
                    </DrawerContent>
                </Drawer>
            )}

            {/* Full Screen Image Viewer Modal */}
            {fullScreenImage && (
                <div
                    className="fixed inset-0 z-[10005] bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm pointer-events-auto"
                    onClick={() => setFullScreenImage(null)}
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setFullScreenImage(null);
                        }}
                        className="absolute top-4 right-4 text-white hover:text-white/80 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
                    >
                        <FontAwesomeIcon icon={faXmark} className="text-xl" />
                    </button>
                    <img
                        src={fullScreenImage}
                        alt="Full Screen View"
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
}
function handleSendTranscript(finalToSend: string) {
    throw new Error("Function not implemented.");
}

